import React, {
    useState, useEffect, useRef, useCallback,
} from 'react';
import { axiosInstance } from '@appblocks/frontend/client/utils';
import { Link } from 'react-router-dom';
import Table from 'react-bootstrap/Table';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Dropdown from 'react-bootstrap/Dropdown';
import { useDebounceCallback, useInterval } from 'usehooks-ts';
import { MdOutlineRefresh, MdFilterListAlt, MdClose } from 'react-icons/md';
import moment from 'moment';
import PaginationComponent from '@appblocks/frontend/client/Components/PaginationComponent';
import TimeRangePicker from '@appblocks/frontend/cloudclient/Components/TimeRangePicker';
import ExpressionBuilder from '@appblocks/frontend/cloudclient/Components/ExpressionBuilder';
import { ButtonGroup } from 'react-bootstrap';
import RefreshButton from '@appblocks/frontend/cloudclient/Components/RefreshButton';
import { debounce } from 'lodash';


export default function DataPaginationRenderer(props) {
    const [query, setQuery] = useState('');
    const [inputValue, setInputValue] = useState('');
    const debounced = useDebounceCallback(setQuery, 500);
    const [loading, setLoading] = useState(false);
    const [perPage, setPerPage] = useState(props.perPage || 10);
    const [page, setPage] = useState(0);
    const [timeRange, setTimeRange] = useState({
        startTime: moment().subtract(1, 'hours').toDate(),
        endTime: new Date(),
        range: 60 * 1,
    });
    const [showFilters, setShowFilters] = useState(false);
    const [newFilters, setNewFilters] = useState([]);
    const [filters, setFilters] = useState([]);
    const [autoRefresh, setAutoRefresh] = useState('1');

    useEffect(() => {
        doQuery();
    }, [page, query, timeRange, filters]);

    useInterval(async () => {
        if (props.showAutoRefresh && autoRefresh === '1' && props.getItems) {
            if (loading) {
                return;
            }
            try {
                let tmpStartTime = timeRange.startTime;
                let tmpEndTime = timeRange.endTime;
                if (timeRange.range > 0) {
                    tmpStartTime = moment().subtract(timeRange.range, 'minutes').toDate();
                    tmpEndTime = new Date();
                }
                await props.getItems({
                    search: query,
                    start: page * perPage,
                    startTime: tmpStartTime,
                    endTime: tmpEndTime,
                    count: perPage,
                    isRefresh: true,
                    filters,
                });
                setLoading(true);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        }
    }, autoRefresh === '1' ? 8000 : undefined);

    const doQuery = async () => {
        if (props.getItems) {
            setLoading(true);
            try {
                let tmpStartTime = timeRange.startTime;
                let tmpEndTime = timeRange.endTime;
                if (timeRange.range > 0) {
                    tmpStartTime = moment().subtract(timeRange.range, 'minutes').toDate();
                    tmpEndTime = new Date();
                }
                await props.getItems({
                    search: query,
                    start: page * perPage,
                    count: perPage,
                    startTime: tmpStartTime,
                    endTime: tmpEndTime,
                    filters,
                });
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        }
    };

    const onTimeRangeChanged = ({ startTime, endTime, range }) => {
        setTimeRange({
            startTime,
            endTime,
            range,
        });
    };

    const toggleFiltersModal = () => {
        setNewFilters([...filters]);
        setShowFilters(!showFilters);
    };

    const applyFilters = (e) => {
        if (e !== undefined) {
            e.preventDefault();
        }
        setShowFilters(false);
        setFilters(newFilters);
    };

    const onFiltersChanged = (_, tmpFilters) => {
        setNewFilters(tmpFilters.rules);
    };

    const removeFilter = (index) => {
        const tmpFilters = [...filters];
        tmpFilters.splice(index, 1);
        setFilters(tmpFilters);
    };

    const renderRefresh = () => {
        return (
            <RefreshButton
                loading={loading}
                doQuery={doQuery}
                showAutoRefresh={props.showAutoRefresh}
                autoRefresh={autoRefresh}
                setAutoRefresh={setAutoRefresh}
            />
        );
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        setInputValue(value);
        debounced(value);
    };

    const clearSearch = () => {
        setInputValue('');
        debounced('');
    };

    return (
        <>
            <div className="row mb-3">
                {(props.search !== undefined ? props.search : true) && (
                    <div className="col-3">
                        <div className="position-relative">
                            <input
                                type="text"
                                className="form-control"
                                placeholder={props.searchPlaceholder || 'Search'}
                                onChange={handleInputChange}
                                value={inputValue}
                            />
                            {inputValue && (
                                <button
                                    type="button"
                                    className="btn btn-sm position-absolute"
                                    style={
                                        {
                                            right: '2px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            border: 'none',
                                            background: 'transparent',
                                        }
                                    }
                                    onClick={clearSearch}
                                    aria-label="Clear search"
                                >
                                    <MdClose size={20} />
                                </button>
                            )}
                        </div>
                    </div>
                )}
                {props.filter !== false && (
                    <div className={`${props.search !== false ? 'col-md-9' : 'col-md-12'} align-middle`}>
                        <div className="row justify-content-end">
                            <div className="col-sm">
                                {props.timeRange && (
                                    <TimeRangePicker
                                        onTimeRangeChanged={onTimeRangeChanged}
                                    />
                                )}
                            </div>
                            <div className="col-lg-2 col-md-4">
                                {renderRefresh()}
                                <button
                                    type="button"
                                    className="btn btn-secondary position-relative col-sm float-end"
                                    onClick={toggleFiltersModal}
                                >
                                    <MdFilterListAlt />
                                    {filters.length > 0 && (
                                        <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                                            {filters.length}
                                        </span>
                                    )}
                                </button>

                            </div>
                        </div>
                    </div>
                )}
            </div>
            {(loading && props.total === 0) ? (
                <p className="placeholder-glow">
                    <span className="placeholder col-12 placeholder-lg" />
                    <span className="placeholder col-12" />
                    <span className="placeholder col-12" />
                    <span className="placeholder col-12" />
                    <span className="placeholder col-12" />
                    <span className="placeholder col-12" />
                </p>
            ) : (
                <>
                    {props.children}
                </>
            )}
            {props.total >= perPage && (
                <PaginationComponent
                    perPage={perPage}
                    page={page}
                    total={props.total}
                    setPage={setPage}
                />
            )}
            <Modal show={showFilters} onHide={toggleFiltersModal} size="lg">
                <Form onSubmit={applyFilters}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <span>Filters</span>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <ExpressionBuilder
                            dataTestId={`${props.dataTestId}-filters`}
                            value={{
                                rules: newFilters,
                            }}
                            state={{}}
                            onChange={onFiltersChanged}
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="primary"
                            type="submit"
                        >
                            Apply
                        </Button>
                        <Button variant="secondary" onClick={toggleFiltersModal}>
                            Cancel
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </>

    );
}
